import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { Shield, Mail, Calendar, User, ChevronDown, Plus, Wand2 } from 'lucide-react';
import { cn } from '../lib/utils';

export const Groups: React.FC = () => {
  const { user, refreshProfile, isProcessing } = useAuth();

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {user?.avatar_url ? (
            <img 
              src={user.avatar_url} 
              alt={user.full_name}
              referrerPolicy="no-referrer"
              className="w-12 h-12 rounded-full border border-amber-500/20 object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-600 border border-amber-500/20">
              <User className="w-6 h-6" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight">{user?.full_name}</h1>
              {user?.role === 'Admin' && (
                <span className="bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest">
                  Admin
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground font-mono">{user?.email}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="hidden md:flex flex-col items-end justify-center mr-4">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Livello Accesso</p>
            <p className={cn(
              "text-xs font-bold",
              user?.role === 'Admin' ? "text-rose-500" : "text-amber-600"
            )}>
              {user?.role === 'Admin' ? "Lettura/Scrittura Totale" : "Limitato ai Gruppi"}
            </p>
          </div>
          <button 
            onClick={() => refreshProfile()}
            disabled={isProcessing}
            className={cn(
              "text-xs font-bold text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wider border px-3 py-1.5 rounded-md flex items-center",
              isProcessing && "opacity-50 cursor-not-allowed"
            )}
          >
            {isProcessing ? "Sincronizzazione..." : "Sincronizza Gruppi"}
          </button>
          {user?.role === 'Admin' && (
            <button 
              onClick={() => {
                window.open('https://admin.google.com/ac/groups', '_blank');
              }}
              className="bg-primary text-primary-foreground px-4 py-1.5 rounded-md text-xs font-bold flex items-center hover:opacity-90 transition-opacity uppercase tracking-wider"
            >
              <Wand2 className="w-3.5 h-3.5 mr-2" />
              Gestisci in Google Admin
            </button>
          )}
        </div>
      </div>

      {user?.authError && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-4 shadow-sm">
          <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 shrink-0">
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-rose-900 mb-1 uppercase tracking-tight">Errore Permessi Google Directory</h3>
            <p className="text-xs text-rose-700 leading-relaxed">
              {user.authError}
            </p>
            <div className="mt-3 flex gap-4">
              <a 
                href="https://admin.google.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[10px] font-bold text-rose-800 uppercase tracking-widest hover:underline"
              >
                Apri Console Admin
              </a>
              <button 
                onClick={() => refreshProfile()}
                className="text-[10px] font-bold text-rose-800 uppercase tracking-widest hover:underline"
              >
                Riprova Sincronizzazione
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border rounded-xl shadow-sm overflow-hidden">
        <div className="border-b px-6 py-3 bg-muted/20 flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Gruppi</h3>
          <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-full">
            {user?.groups.length || 0} Gruppi
          </span>
        </div>
        
        <div className="p-0">
          {(!user?.groups || user.groups.length === 0) && (
            <div className="p-12 text-center">
              <div className="flex flex-col items-center max-w-md mx-auto">
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                  <Mail className="w-8 h-8 text-amber-400 opacity-50" />
                </div>
                <h3 className="text-sm font-bold text-foreground mb-1">Nessun gruppo trovato</h3>
                <p className="text-xs text-muted-foreground mb-6">
                  Non abbiamo trovato iscrizioni a gruppi Google Workspace per l'account <span className="font-mono font-bold">{user?.email}</span>.
                </p>
                
                <div className="w-full bg-amber-50/50 border border-amber-100 rounded-lg p-4 text-left">
                  <p className="text-[10px] font-bold text-amber-800 uppercase tracking-tight mb-2 flex items-center">
                    <Shield className="w-3 h-3 mr-1.5" />
                    Possibili cause
                  </p>
                  <ul className="text-[10px] text-amber-700 space-y-1.5 list-disc ml-3">
                    <li>L'utente non è effettivamente membro di alcun gruppo nel dominio.</li>
                    <li>L'API <strong>Admin SDK</strong> non è abilitata nel progetto Google Cloud.</li>
                    <li>Mancano i permessi di lettura sulla Directory Google.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {user?.groups && user.groups.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="text-muted-foreground border-b bg-muted/5">
                    <th className="px-6 py-3 font-bold uppercase tracking-tighter text-[10px] w-12">
                      <div className="w-4 h-4 border rounded bg-background" />
                    </th>
                    <th className="px-6 py-3 font-bold uppercase tracking-tighter text-[10px]">
                      <div className="flex items-center gap-1">
                        Nome del gruppo
                        {user?.role === 'Admin' && <ChevronDown className="w-3 h-3 rotate-180" />}
                      </div>
                    </th>
                    <th className="px-6 py-3 font-bold uppercase tracking-tighter text-[10px]">Indirizzo email</th>
                    <th className="px-6 py-3 font-bold uppercase tracking-tighter text-[10px]">
                      {user?.role === 'Admin' ? "Membri" : "Ruolo"}
                    </th>
                    {user?.role === 'Admin' && (
                      <th className="px-6 py-3 font-bold uppercase tracking-tighter text-[10px]">Tipo di accesso</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {user?.groups.map((group, idx) => (
                    <tr key={idx} className="hover:bg-muted/10 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="w-4 h-4 border rounded bg-background group-hover:border-primary transition-colors" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col min-h-[40px] justify-center">
                          <span className="font-bold text-foreground">
                            {group.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground font-mono text-[11px]">
                        {group.email}
                      </td>
                      <td className="px-6 py-4">
                        {user?.role === 'Admin' ? (
                          <span className="text-[11px] font-bold">{group.membersCount || 0}</span>
                        ) : (
                          <div className="flex items-center justify-between max-w-[120px] bg-muted/30 px-2 py-1 rounded border border-transparent hover:border-muted-foreground/20 cursor-pointer transition-all">
                            <span className="text-[11px] font-bold">{group.role}</span>
                            <ChevronDown className="w-3 h-3 text-muted-foreground" />
                          </div>
                        )}
                      </td>
                      {user?.role === 'Admin' && (
                        <td className="px-6 py-4 text-[11px] text-muted-foreground">
                          Personalizzata
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        <div className="border-t px-6 py-3 bg-muted/5 flex items-center justify-between text-[10px] text-muted-foreground font-bold">
          <p>Sincronizzato con Google Directory API</p>
          <div className="flex items-center gap-4">
            <p>Righe per pagina: 50</p>
            <p>1-{user?.groups.length || 0} di {user?.groups.length || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
